// --------------------------------------------------------------
// Created On: 2025-07-14
// Author: Zachary Thomas
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import Spinner from "../Spinner/Spinner";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import apiRequest from "../../utilities/api/apiRequest";
import { API, GATEWAY_TIMEOUT_STATUS } from "../../constants/miscellaneous";
import useReduxSelector from "../../hooks/useReduxSelector";
import getApiError from "../../utilities/api/getApiError";

// Modal for confirming if a specific monitoring device should get a firmware update.
// When confirmed, this modal initiates the firmware update for the device.
export default function ConfirmFirmwareUpdateModal(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Cleans up state and calls parent 'props.onClose' function.
  function handleClose(): void {
    setErrorMessage("");
    setLoading(false);
    props.onClose();
  }

  // Attempts to update the firmware. If successfully initiated, calls 'props.onUpdate'.
  async function handleYes(): Promise<void> {
    if (props.deviceId === null) {
      setErrorMessage("Monitoring device could not be found. Please try again.");
    } else {
      setLoading(true);
      const [response] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/device/${props.deviceId}/firmware`,
        "POST",
        null
      )) as [Response, UpdateFirmwareResponseBody];
      setLoading(false);

      if (response.ok) {
        setErrorMessage("");
        props.onUpdate();
      } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
        setErrorMessage(
          "Request timeout. This could be due to the device being in the middle of a firmware update" +
            " or the device is currently unreachable."
        );
      } else {
        setErrorMessage(await getApiError(response, "Failed while attempting to update firmware."));
      }
    }
  }

  // Cleans up state and calls parent 'props.onCancel' function.
  function handleNo(): void {
    setErrorMessage("");
    setLoading(false);
    props.onCancel();
  }

  return (
    <Fragment>
      <Spinner loading={loading} />
      <ConfirmModal
        showModal={props.showModal}
        title="Update Firmware?"
        content={
          "Are you sure you want to update the firmware on this device? " +
          "The device will be unusable for up to 30 minutes. " +
          "We will notify you when the update is complete."
        }
        yesText="Update Firmware"
        noText="Cancel"
        danger={true}
        errorMessage={errorMessage}
        disabledYes={loading}
        onClose={() => handleClose()}
        onYes={() => handleYes()}
        onNo={() => handleNo()}
      />
    </Fragment>
  );
}

interface Props {
  showModal: boolean;
  deviceId: number | null;
  onClose: () => void;
  onUpdate: () => void;
  onCancel: () => void;
}

interface UpdateFirmwareResponseBody {
  message: string;
}
